// src/components/POS/payment/OrderSuccessModal.js
import React, { useEffect, useState } from "react";
import { Check, Printer } from "lucide-react";

export default function OrderSuccessModal({ isOpen, onClose, orderNumber, onReprint }) {
    const [show, setShow] = useState(false);

    // Animate modal appearance
    useEffect(() => {
        if (isOpen) {
            setShow(true); // Start animation
        } else {
            setShow(false);
        }
    }, [isOpen]);

    if (!isOpen) return null;

    return (
        <div
            data-pos-modal="true"
            className="fixed inset-0 z-[70] flex items-center justify-center bg-slate-950/45 px-4 py-6 backdrop-blur-sm"
        >
            <div
                className={`w-full max-w-md overflow-hidden rounded-[28px] bg-white shadow-[0_28px_90px_rgba(15,23,42,0.32)] transform transition-all duration-300 ${show ? "opacity-100 scale-100 translate-y-0" : "opacity-0 scale-95 translate-y-2"}`}
            >
                <div className="flex flex-col items-center px-6 pb-6 pt-7 text-center">
                    <div className="relative mb-5 flex h-20 w-20 items-center justify-center">
                        <span
                            className="absolute inset-0 rounded-full animate-[successRipple_2.25s_ease-out_infinite]"
                            style={{ background: "var(--color-primary)" }}
                        />
                        <span
                            className="absolute inset-0 rounded-full animate-[successRipple_2.25s_ease-out_infinite_750ms]"
                            style={{ background: "var(--color-primary)" }}
                        />
                        <span
                            className="absolute inset-2 rounded-full opacity-10 animate-[successHalo_1.8s_ease-in-out_infinite]"
                            style={{ background: "var(--color-primary)" }}
                        />
                        <span
                            className="relative flex h-16 w-16 items-center justify-center rounded-full text-white shadow-[0_16px_40px_rgba(15,23,42,0.18)] animate-[successCore_1.8s_ease-in-out_infinite]"
                            style={{ background: "var(--color-primary)" }}
                        >
                            <Check className="h-9 w-9 animate-[successCheck_520ms_cubic-bezier(.2,.9,.25,1.25)_120ms_both]" strokeWidth={3.2} />
                        </span>
                    </div>

                    <p className="mb-2 text-[11px] font-bold uppercase tracking-[0.2em] text-slate-400">Payment Complete</p>
                    <h3 className="text-2xl font-bold tracking-tight text-slate-950">
                        Order Created
                    </h3>

                    <div className="mt-5 w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3">
                        <p className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-400">Receipt No.</p>
                        <p className="mt-1 break-all text-sm font-bold text-slate-800">{orderNumber}</p>
                    </div>
                </div>

                <div className="grid w-full grid-cols-1 gap-3 border-t border-slate-200 bg-slate-50/80 px-6 py-5 sm:grid-cols-2">
                    <button
                        type="button"
                        onClick={onReprint}
                        className="inline-flex h-12 items-center justify-center gap-2 rounded-2xl border border-slate-200 bg-white px-4 text-sm font-semibold text-slate-700 shadow-sm transition hover:border-slate-300 hover:bg-slate-50 focus:outline-none focus:ring-4 focus:ring-slate-100"
                    >
                        <Printer size={16} />
                        Reprint
                    </button>
                    <button
                        type="button"
                        onClick={onClose}
                        className="h-12 rounded-2xl px-4 text-center text-sm font-semibold text-white shadow-sm transition-colors duration-200 focus:outline-none focus:ring-4 focus:ring-slate-100"
                        style={{ background: "var(--color-primary)" }}
                        onMouseOver={(event) => {
                            event.currentTarget.style.background = "color-mix(in srgb, var(--color-primary) 82%, black)";
                        }}
                        onMouseOut={(event) => {
                            event.currentTarget.style.background = "var(--color-primary)";
                        }}
                    >
                        Close
                    </button>
                </div>
            </div>
            <style>{`
                @keyframes successRipple {
                    0% { transform: scale(0.72); opacity: 0.24; }
                    58% { transform: scale(1.18); opacity: 0.09; }
                    100% { transform: scale(1.34); opacity: 0; }
                }

                @keyframes successHalo {
                    0%, 100% { transform: scale(0.92); opacity: 0.12; }
                    50% { transform: scale(1.12); opacity: 0.2; }
                }

                @keyframes successCore {
                    0%, 100% { transform: scale(1); }
                    50% { transform: scale(1.04); }
                }

                @keyframes successCheck {
                    0% { transform: scale(0.35) rotate(-14deg); opacity: 0; }
                    62% { transform: scale(1.08) rotate(0deg); opacity: 1; }
                    100% { transform: scale(1) rotate(0deg); opacity: 1; }
                }
            `}</style>
        </div>
    );
}
